/**
 * Persistent app shell — TASK 7.2 Steps 1, 2, 4, 7.
 *
 * The mock's shell block (L85-836), extracted verbatim:
 *
 *   <div>                                        (L86 — bare wrapper, kept)
 *     <header style="position:sticky;top:0;z-index:40;…">        -> .sm-header
 *       <div style="max-width:820px;…">                          -> .sm-header__inner
 *         <a href="#/" aria-label="SteamyMusic home">SteamyMusic</a>  -> .sm-wordmark
 *         <div style="flex:1"></div>                             -> .sm-spacer
 *         <a href="#/queue" aria-label="Queue">QUEUE</a>          -> .sm-header-link
 *         <a href="#/settings" aria-label="Settings and profile">Y</a> -> .sm-avatar
 *     <main id="main" style="max-width:820px;…padding:16px 14px 210px"> -> .sm-main
 *     <nav aria-label="Primary" …>                               -> ./PrimaryNav.js
 *     mini-player dock / overlays / toast rail / live announcer
 *
 * ui-contract §2 states the same tree: `header(banner) -> main -> nav[Primary]`.
 *
 * ── TWO DELIBERATE STRUCTURAL DELTAS ──────────────────────────────────────
 * 1. The mock nested overlays, the toast rail and the live announcer INSIDE
 *    the L86 wrapper. Here the wrapper holds only header + main + nav +
 *    mini-player, because that subtree is what gets `inert`/`aria-hidden`
 *    while a modal is open. Overlays obviously must not be inert; the two
 *    live regions must stay announceable. Both therefore mount as siblings.
 * 2. The header's `⌁ OFFLINE` (L92) and `FAIL MODE` (L95) pills are NOT
 *    rendered. They were readouts of the sim flags `flags.offline` /
 *    `flags.fail`, which dev-plan TASK 7.3 Step 1b deletes outright. Real
 *    connectivity status arrives with the service worker (TASK 10.1); there
 *    is no real analogue of "fail mode" at all. Both were hidden in the
 *    default state the fidelity baseline captures.
 *
 * ── `session_started` (TASK 7.2 Step 7 / W17) ─────────────────────────────
 * {@link createSessionStartedWatcher} is the shell's half of the telemetry
 * seam: it fires on SESSION GAIN (unauthenticated -> authenticated, i.e. cold
 * start with a session or a completed sign-in) and on RESUME AFTER >= 30 min
 * idle, and never on an ordinary re-render. It calls the emitter registered
 * through `state-store.js#setTelemetryEmitter`, whose default is a no-op —
 * `src/lib/telemetry-client.ts` swaps it in without touching this file.
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { audioEngine } from '../../lib/audio-engine.js';

import { append, el } from '../components/primitives/dom.js';
import { albumArtUrl, gradientFor, initialsOf } from '../components/primitives/format.js';
import { MiniPlayer } from '../views/player/MiniPlayer.js';

import { createLiveRegions } from './LiveRegions.js';
import { createOverlayStack } from './OverlayStack.js';
import { openMoreMenu } from './MoreMenu.js';
import { PrimaryNav } from './PrimaryNav.js';
import { createRouteAnnouncer } from './RouteAnnouncer.js';
import { FullPlayer } from '../views/player/FullPlayer.js';

import { PageHeading, navigate } from './router.js';
import { getTelemetryEmitter, subscribe as subscribeToStore } from './state-store.js';

/** Resume-after-idle boundary for `session_started` (dev-plan: >= 30 min). */
export const SESSION_IDLE_RESUME_MS = 30 * 60 * 1000;

/** The `client` dimension of the `session_started` payload (§11). */
export const TELEMETRY_CLIENT = 'web';

/**
 * `pwa_installed` (§11): true when the document is running as an installed
 * app. `display-mode: standalone` covers Chromium/Firefox; `navigator.standalone`
 * covers iOS Safari, which does not implement the media query.
 *
 * @param {Window} [win]
 * @returns {boolean}
 */
export function isPwaInstalled(win = window) {
  const legacy = /** @type {{ standalone?: boolean }} */ (win.navigator).standalone;
  if (legacy === true) return true;
  if (typeof win.matchMedia !== 'function') return false;
  return win.matchMedia('(display-mode: standalone)').matches === true;
}

/**
 * @typedef {object} SessionStartedWatcher
 * @property {(session: { authenticated: boolean }) => boolean} observe
 *   feed every state change here; returns true when it emitted
 * @property {() => void} markHidden call when the document is hidden
 * @property {() => boolean} markVisible call when it becomes visible again;
 *   returns true when the idle threshold was crossed and it emitted
 */

/**
 * @param {{
 *   emit?: (event: string, payload: Record<string, string|number|boolean>) => void,
 *   now?: () => number,
 *   pwaInstalled?: () => boolean,
 *   idleMs?: number,
 *   client?: string,
 * }} [options]
 * @returns {SessionStartedWatcher}
 */
export function createSessionStartedWatcher(options = {}) {
  const {
    // Resolved at CALL time, not capture time, so registration order between
    // the shell and telemetry-client.ts cannot matter.
    emit = (event, payload) => getTelemetryEmitter().emit(event, payload),
    now = () => Date.now(),
    pwaInstalled = () => isPwaInstalled(),
    idleMs = SESSION_IDLE_RESUME_MS,
    client = TELEMETRY_CLIENT,
  } = options;

  let authenticated = false;
  let lastActiveAt = now();

  function fire() {
    emit('session_started', { client, pwa_installed: pwaInstalled() });
    lastActiveAt = now();
  }

  return {
    observe(session) {
      const next = session.authenticated === true;
      const gained = next && !authenticated;
      authenticated = next;
      if (!gained) return false; // ordinary re-render (or sign-out)
      fire();
      return true;
    },
    markHidden() {
      lastActiveAt = now();
    },
    markVisible() {
      if (!authenticated) return false;
      if (now() - lastActiveAt < idleMs) {
        lastActiveAt = now();
        return false;
      }
      fire();
      return true;
    },
  };
}

/* ── Header (mock L87-100) ─────────────────────────────────────────────── */

/**
 * @param {{ initial: string }} options
 * @returns {HTMLElement}
 */
function Header(options) {
  const inner = el('div', { className: 'sm-header__inner' });
  append(
    inner,
    // mock L89
    el('a', {
      className: 'sm-wordmark',
      text: 'SteamyMusic',
      testId: TESTIDS.STEAMYMUSIC_HOME_LINK,
      attrs: { href: '#/', 'aria-label': 'SteamyMusic home' },
    }),
    el('div', { className: 'sm-spacer' }), // mock L90
    // mock L97
    el('a', {
      className: 'sm-header-link',
      text: 'QUEUE',
      testId: TESTIDS.QUEUE_LINK,
      attrs: { href: '#/queue', 'aria-label': 'Queue' },
    }),
    // mock L98
    el('a', {
      className: 'sm-avatar',
      text: options.initial,
      testId: TESTIDS.SETTINGS_AND_PROFILE_LINK,
      attrs: { href: '#/settings', 'aria-label': 'Settings and profile' },
    }),
  );
  return append(el('header', { className: 'sm-header' }), inner);
}

/**
 * The header avatar glyph. The mock hardcoded "Y" (L98); the real one is the
 * first letter of the signed-in user's name, falling back to the mock's glyph
 * so the fidelity baseline still matches when no session is loaded.
 *
 * @param {{ name?: string, email?: string }|null} user
 * @returns {string}
 */
export function avatarInitial(user) {
  const source = (user?.name ?? user?.email ?? '').trim();
  return source === '' ? 'Y' : source.slice(0, 1).toUpperCase();
}

/* ── Shell ────────────────────────────────────────────────────────────── */

/**
 * @typedef {object} ShellHandle
 * @property {HTMLElement} element the `.sm-app` root
 * @property {HTMLElement} main the `#main` scroll region
 * @property {import('./OverlayStack.js').OverlayStackHandle} overlays
 * @property {ReturnType<typeof createLiveRegions>} live
 * @property {ReturnType<typeof PrimaryNav>} nav
 * @property {SessionStartedWatcher} sessionWatcher
 * @property {(resolved: import('./router.js').RouteDefinition extends never ? never : {
 *   route: import('./router.js').RouteDefinition,
 *   params: Record<string, string>,
 *   path: string,
 * }, body?: HTMLElement) => void} renderRoute
 * @property {(node: HTMLElement) => void} renderStandalone
 * @property {(state: import('./state-store.js').AppState) => void} syncState
 * @property {(options?: { fromRoute?: boolean }) => void} openFullPlayer
 * @property {() => void} dispose
 */

/**
 * Builds and mounts the shell into `root`.
 *
 * @param {{
 *   root: HTMLElement,
 *   getState: () => import('./state-store.js').AppState,
 *   player?: import('../../lib/audio-engine.js').AudioEngine,
 *   onFavoriteCurrent?: () => void,
 *   onTrackActionsForCurrent?: () => void,
 *   onFetchLyrics?: (trackId: string) => Promise<import('@streamymusic/shared').LyricsResponse>,
 *   go?: (href: string) => void,
 *   win?: Window,
 *   doc?: Document,
 * }} options
 *   `player` is the TASK 8.2 transport the docked mini-player and the full
 *   player drive. It defaults to the process-wide engine so the shell keeps
 *   working standalone; `startApp` passes its own so a test can substitute
 *   one. `onFavoriteCurrent`/`onTrackActionsForCurrent` are the two full-player
 *   actions that need the api-client (favourites) and the overlay stack
 *   (sheets), both of which the COMPOSITION ROOT owns, not the shell.
 * @returns {ShellHandle}
 */
export function mountShell(options) {
  const {
    root,
    getState,
    player = audioEngine,
    onFavoriteCurrent = () => live.announce('Favorite toggled'),
    onTrackActionsForCurrent = () => live.announce('Track actions'),
    onFetchLyrics = () => Promise.reject(new Error('Lyrics source unavailable')),
    go = navigate,
    win = window,
    doc = document,
  } = options;

  const app = el('div', { className: 'sm-app' });
  // mock L86 — the bare wrapper. Doubles as the inert background (see header).
  const background = el('div');

  const header = Header({ initial: avatarInitial(getState().session.user) });
  const main = el('main', { className: 'sm-main', attrs: { id: 'main' } });

  const nav = PrimaryNav({
    onMore: () => openMoreMenu({ stack: overlays, role: getState().session.role, go }),
  });

  append(background, header, main, nav.element);
  app.append(background);

  const live = createLiveRegions();
  append(app, ...live.elements);

  root.replaceChildren(app);

  const overlays = createOverlayStack({ host: app, background, doc, win });
  const announcer = createRouteAnnouncer({ announce: live.announce, doc, win });
  const sessionWatcher = createSessionStartedWatcher();

  /** @type {import('../views/player/MiniPlayer.js').MiniPlayerHandle|null} */
  let miniPlayer = null;
  /** Which track the mounted dock was BUILT for; a change is the only rebuild. */
  let dockedTrackId = null;
  /** The standalone (shell-less) screen currently mounted, if any. */
  let standalone = null;

  /**
   * Tears down a standalone auth screen and restores the shell subtree.
   *
   * @returns {void}
   */
  function clearStandalone() {
    if (standalone === null) return;
    standalone.remove();
    standalone = null;
    // `hidden` is a DOM property, not a `style=` attribute — CSP-clean (W3).
    background.hidden = false;
  }

  /**
   * Renders a SHELL-LESS screen (ui-contract §2: `/signin`, `/signin/denied`
   * and `/signin/error` are "standalone (no shell/landmarks; centered flex)").
   * The header/main/nav subtree is hidden rather than unmounted so returning to
   * a shell route costs no re-mount and no loss of overlay/live-region state.
   *
   * @param {HTMLElement} node the screen built by `views/auth/*`
   * @returns {void}
   */
  function renderStandalone(node) {
    announcer.beforeRender(app);
    if (standalone !== null) standalone.remove();
    standalone = node;
    /*
     * Empty `main` as well as hiding the shell. `hidden` takes the subtree out
     * of the a11y tree, but it stays in the DOM — so the route the user came
     * FROM would leave a second `[data-h1][data-testid="page-heading"]`
     * behind, and every Tier-1 selector that resolves `page-heading` would
     * pick the stale hidden one. Caught in Chromium on the
     * /settings -> sign-out -> /signin transition.
     */
    main.replaceChildren();
    background.hidden = true;
    // Before the live regions so they stay last in the mount order.
    app.insertBefore(node, live.toastRail);
    nav.setActiveSection(null);
    announcer.afterNavigate({ container: node, scrollTarget: null });
  }

  /**
   * Renders the route's `<section>` into `main`. The per-route BODIES are
   * Stories 8-10; what TASK 7.3 owns — and what every route must carry — is
   * the `<h1 data-h1 tabindex="-1" data-testid="page-heading">` focus target
   * (ui-contract §3) inside a `data-screen-label`led section.
   *
   * @param {{ route: import('./router.js').RouteDefinition, params: Record<string, string>, path: string }} resolved
   * @param {HTMLElement} [body] a fully-built `<section>` (TASK 7.4's
   *   `/settings`, Stories 8-10's route bodies) replacing the default heading-
   *   only one. It must carry its own `data-screen-label` + `[data-h1]`.
   */
  function renderRoute(resolved, body) {
    const { route } = resolved;
    clearStandalone();
    announcer.beforeRender(main);

    const section = body ?? el('section', { attrs: { 'data-screen-label': route.screenLabel } });
    if (body === undefined) append(section, PageHeading(route));
    main.replaceChildren(section);

    nav.setActiveSection(route.navSection);
    announcer.afterNavigate({ container: main, scrollTarget: main });

    // The full player is an overlay, not a page (mock L730). Entering
    // `/player` opens it; leaving closes whatever the stack still holds.
    if (route.id === 'player') {
      if (overlays.entries().every((entry) => entry.kind !== 'player')) {
        openFullPlayer({ fromRoute: true });
      }
    }
  }

  /**
   * Opens the full player.
   *
   * `fromRoute` distinguishes the two ways it can appear:
   *   - deep link / mini-player tap (`fromRoute: true`) — dismissing it should
   *     also leave `/player`, exactly as the mock's `fp.close` did
   *     (L1144: `if(history.length>1)history.back(); else this._go('#/')`).
   *     The navigation is deferred to a microtask so it never runs INSIDE the
   *     `popstate` dispatch that dismissed the overlay.
   *   - opened over another overlay (`fromRoute: false`) — pure overlay, no
   *     route involvement. This is AC 0.4's player-over-modal case: one back
   *     pops only the player and the route does not change.
   *
   * @param {{ fromRoute?: boolean }} [opts]
   */
  function openFullPlayer(opts = {}) {
    const { fromRoute = false } = opts;

    /**
     * The full player is the only overlay that has to REPAINT continuously —
     * position, buffering, banner, play/pause. It is built once and updated
     * from the store, never rebuilt: a rebuild would destroy the node the
     * overlay stack's focus trap is holding and drop focus mid-drag.
     *
     * @returns {import('../views/player/FullPlayer.js').FullPlayerModel}
     */
    const modelOf = () => {
      const { playback, catalog } = getState();
      const current = playback.current;
      return {
        title: current?.title ?? 'Nothing playing',
        artist: current === null ? '' : `${current.artist} — ${current.album}`,
        initials: initialsOf(current?.album ?? current?.title ?? ''),
        background: gradientFor(current?.albumId ?? 'none'),
        // The key the lyrics panel fetches + caches by; a CHANGE here is what
        // makes it drop a stale response and load the new track's lyrics.
        trackId: current?.id ?? null,
        // Real cover art over the gradient — the SAME has-real-art rule the
        // album tiles use (`albumArtUrl`), keyed on the album, not the track.
        // `undefined` (no real cover) leaves the gradient + initials standing.
        artUrl:
          current === null
            ? undefined
            : albumArtUrl({ id: current.albumId, coverRef: current.coverRef ?? null }),
        playing: playback.playing,
        buffering: playback.buffering,
        shuffle: playback.shuffle,
        repeat: playback.repeat,
        favorite: current !== null && catalog.favorites.tracks.includes(current.id),
        position: playback.pos,
        duration: playback.dur,
        banner: playback.banner,
      };
    };

    const descriptor = FullPlayer({
      model: modelOf(),
      handlers: {
        onClose: () => overlays.closeTop(),
        onPrevious: () => player.previous(),
        onNext: () => player.next(),
        onToggle: () => player.toggle(),
        onShuffle: () => player.setShuffle(!getState().playback.shuffle),
        onRepeat: () => player.cycleRepeat(),
        onSeek: (seconds) => player.seek(seconds),
        onRetry: () => player.retry(),
        onFavorite: () => onFavoriteCurrent(),
        onTrackActions: () => onTrackActionsForCurrent(),
        // The api-client's `getTrackLyrics` lives in the composition root, like
        // the ♥ / ⋯ actions above — the shell just forwards it to the panel.
        onFetchLyrics: (trackId) => onFetchLyrics(trackId),
      },
    });

    const unsubscribePlayer = subscribeToStore(() => {
      descriptor.update(modelOf());
    });

    descriptor.onDismiss = () => {
      unsubscribePlayer();
      if (!fromRoute) return;
      // Deferred: never touch history from inside a popstate dispatch.
      win.queueMicrotask(() => {
        if (win.location.hash.startsWith('#/player')) {
          if (win.history.length > 1) win.history.back();
          else go('#/');
        }
      });
    };

    overlays.open(descriptor);
  }

  /**
   * Reflects store changes that the SHELL owns: the avatar glyph, the docked
   * mini-player (AC 0.3), the toast rail's offset, and `session_started`.
   *
   * @param {import('./state-store.js').AppState} state
   */
  function syncState(state) {
    sessionWatcher.observe(state.session);

    const avatar = header.querySelector(`[data-testid="${TESTIDS.SETTINGS_AND_PROFILE_LINK}"]`);
    if (avatar !== null) avatar.textContent = avatarInitial(state.session.user);

    const current = state.playback.current;

    /*
     * BUILD ONCE PER TRACK, REPAINT PER TICK.
     *
     * This used to unconditionally `remove()` the dock and construct a new one
     * on EVERY store change — and `modules/app/index.js` routes every `setState`
     * here, while `lib/audio-engine.ts` patches `{pos}` on every `timeupdate`,
     * roughly four times a second. So the app's primary persistent control was
     * destroyed and rebuilt four times a second: keyboard focus inside it went
     * to `<body>` (WCAG 2.4.3 / 2.4.7), the progress animation restarted from
     * frame 0, and ~44 nodes churned per second. `MiniPlayer` now returns the
     * same `{ element, update }` handle `FullPlayer` and `SeekBar` do, and the
     * node is torn down only when the track actually changes or goes away.
     */
    if (current === null) {
      if (miniPlayer !== null) {
        miniPlayer.element.remove();
        miniPlayer = null;
        dockedTrackId = null;
      }
    } else {
      const model = {
        title: current.title,
        subtitle: current.artist,
        initials: initialsOf(current.album),
        background: gradientFor(current.albumId),
        // Real cover art over the 44px gradient thumb — same rule as the tiles
        // and the full player; `undefined` keeps the gradient + initials.
        artUrl: albumArtUrl({ id: current.albumId, coverRef: current.coverRef ?? null }),
        playing: state.playback.playing,
        buffering: state.playback.buffering,
        progress: state.playback.dur === 0 ? 0 : state.playback.pos / state.playback.dur,
      };
      if (miniPlayer === null || dockedTrackId !== current.id) {
        if (miniPlayer !== null) miniPlayer.element.remove();
        miniPlayer = MiniPlayer({
          model,
          // TASK 8.2 — the SAME persistent element the album surface started,
          // so skipping from the dock never needs a fresh gesture.
          handlers: {
            onPrevious: () => player.previous(),
            onNext: () => player.next(),
            onToggle: () => player.toggle(),
          },
        });
        dockedTrackId = current.id;
        background.append(miniPlayer.element);
      } else {
        miniPlayer.update(model);
      }
    }
    live.setDocked(current !== null);
  }

  const handleVisibility = () => {
    if (doc.visibilityState === 'hidden') sessionWatcher.markHidden();
    else sessionWatcher.markVisible();
  };
  doc.addEventListener('visibilitychange', handleVisibility);

  return {
    element: app,
    main,
    overlays,
    live,
    nav,
    sessionWatcher,
    renderRoute,
    renderStandalone,
    syncState,
    openFullPlayer,
    dispose() {
      clearStandalone();
      doc.removeEventListener('visibilitychange', handleVisibility);
      overlays.dispose();
      live.dispose();
    },
  };
}

/* ── Small pure helpers, verbatim from the mock ────────────────────────── */

/*
 * They now live in ../components/primitives/format.js (Story 8 gave them five
 * more callers, one of which the shell itself imports — see that module's
 * header). Re-exported here so `Shell.js`'s published surface is unchanged.
 */
export { formatSeconds, gradientFor, initialsOf } from '../components/primitives/format.js';
